import { Vector3 } from 'three';
import type { CollisionWorld } from '../world/CollisionWorld';
import type { Room } from '../world/Room';
import type { Surface, SurfaceId } from '../world/Surface';
import {
  removeAssetAt,
  removeDroneAt,
  removeWaypointAt,
  nearestDroneIndex,
  appendWaypoint,
  type AssetKind,
  type MapDocument,
} from './MapDocument';
import { CELL_SIZE } from './components';
import {
  BUILD_PIECE_KINDS,
  isBoxPiece,
  pieceSize,
  variantCount,
  type BuildPieceKind,
} from './pieces';

/** What the scanner is about to place, once the aim ray has been resolved. */
export interface BuildPlacement {
  kind: BuildPieceKind;
  /** Where the piece goes: a box centre, or a drone's anchor on the surface. */
  position: [number, number, number];
  size: [number, number, number];
  /** The face the piece is mounted from. */
  surface: SurfaceId;
  /** The face's outward normal, for drawing a marker off the surface. */
  normal: [number, number, number];
  /** True when the spot is legal; a false preview is still drawn, in red. */
  valid: boolean;
}

/** The scanner's reach, metres. */
export const BUILD_REACH = 14;
/** How far from the aim point a delete still finds a piece, metres. */
export const DELETE_TOLERANCE = 1.4;

const scratchPoint = new Vector3();
const scratchLocal = { u: 0, v: 0 };

/**
 * The 3D room builder's placement logic, with no rendering and no DOM.
 *
 * The player aims; this resolves what the ray hits, snaps the piece to the grid
 * *in the plane of that surface*, mounts it there, and reports whether the spot
 * is legal. `Game` owns the walking, the hologram and the input; the geometry
 * lives here so it can be tested without a renderer.
 */
export class BuildController {
  private piece: BuildPieceKind = 'crate';
  private variant = 0;
  private rotated = false;
  private magnetic = false;

  constructor(
    private readonly collision: CollisionWorld,
    /** The room the map compiles to: placed pieces become solid in it. */
    private readonly room: Room,
    private document: MapDocument,
  ) {
    // Deliberately no `syncCollision()` here. The room already holds whatever
    // is solid for this session — a catalog level's cover, or the compiled map
    // when building — and the controller is constructed even outside build mode
    // (with a blank document for a normal run). Syncing on construction would
    // clear the room's obstacles and let bolts, the player and drone
    // line-of-sight pass straight through the level's crates and pillars. The
    // list is refreshed only when the map actually changes (`place`, `remove`,
    // `setDocument`).
  }

  /** Point the controller at the map being edited. */
  setDocument(document: MapDocument): void {
    this.document = document;
    this.syncCollision();
  }

  /**
   * Make the placements solid in the session that placed them.
   *
   * The collision world reads the room's obstacle list on every query, so the
   * pieces are written into it in place: a crate you have just put down is
   * something you can stand on, stack on and bump into, exactly as it will be
   * when the level is reloaded. Without this the scanner places ghosts.
   */
  private syncCollision(): void {
    const obstacles = this.room.obstacles;
    obstacles.length = 0;
    for (const asset of this.document.assets) {
      obstacles.push({
        center: new Vector3(asset.position[0], asset.position[1], asset.position[2]),
        halfSize: new Vector3(asset.size[0] / 2, asset.size[1] / 2, asset.size[2] / 2),
        magnetic: false,
        kind: asset.kind,
      });
    }
  }

  get map(): MapDocument {
    return this.document;
  }

  get currentPiece(): BuildPieceKind {
    return this.piece;
  }

  get currentVariant(): number {
    return this.variant;
  }

  get isRotated(): boolean {
    return this.rotated;
  }

  get isMagnetic(): boolean {
    return this.magnetic;
  }

  /** How many things are placed in the map. */
  get placedCount(): number {
    return this.document.assets.length + this.document.drones.length;
  }

  /** How many route waypoints the map holds, across every drone. */
  get waypointCount(): number {
    return this.document.drones.reduce((total, drone) => total + (drone.route?.length ?? 0), 0);
  }

  /** One line describing the scanner, for the HUD. */
  describe(): string {
    const rotated = this.rotated ? ' · ROTATED' : '';
    if (this.piece === 'waypoint') {
      return `WAYPOINT · ${this.waypointCount} POINTS`;
    }
    if (this.piece === 'drone') {
      return `DRONE · ${this.magnetic ? 'MAGNETIC' : 'STANDARD'}${rotated}`;
    }
    return `${this.piece.toUpperCase()} · ${this.variant + 1}/${variantCount(this.piece)}${rotated}`;
  }

  /** Point the scanner at a specific piece, as a dedicated key does. */
  usePiece(kind: BuildPieceKind): void {
    this.piece = kind;
    this.variant = 0;
  }

  cyclePiece(direction = 1): void {
    const index = BUILD_PIECE_KINDS.indexOf(this.piece);
    const next = (index + direction + BUILD_PIECE_KINDS.length) % BUILD_PIECE_KINDS.length;
    this.piece = BUILD_PIECE_KINDS[next] ?? 'crate';
    this.variant = 0;
  }

  cycleVariant(direction = 1): void {
    const count = variantCount(this.piece);
    this.variant = (this.variant + direction + count) % count;
  }

  rotate(): void {
    this.rotated = !this.rotated;
  }

  toggleMagnetic(): void {
    this.magnetic = !this.magnetic;
  }

  /**
   * Resolve where the current piece would land under an aim ray.
   *
   * A hit on a *box* has no surface of its own, and that is deliberate: it is
   * how a crate ends up stacked on a crate or a drone anchors on a console.
   * `body` is the player's own position, so a piece cannot be dropped on them.
   */
  preview(
    origin: Vector3,
    direction: Vector3,
    reach: number,
    body: Vector3,
  ): BuildPlacement | null {
    const hit = this.collision.raycast(origin, direction, reach);
    if (!hit) return null;

    const normal: [number, number, number] = [hit.normal.x, hit.normal.y, hit.normal.z];
    const surfaceId = faceOf(hit.normal);
    const size = pieceSize(this.piece, this.variant, this.rotated, normal);
    const clear = !overlapsBody(hit.point, size, body);

    if (!isBoxPiece(this.piece)) {
      // A drone anchors to the point the ray hit: the hover height is the
      // drone's own, so the map stores the surface point, exactly as the
      // shipped decks do. A waypoint joins the nearest drone's route, so the
      // spot is only legal when there is a drone within reach of it.
      const attached =
        this.piece === 'waypoint'
          ? nearestDroneIndex(this.document, hit.point.x, hit.point.y, hit.point.z) !== null
          : true;
      return {
        kind: this.piece,
        position: [hit.point.x, hit.point.y, hit.point.z],
        size,
        surface: surfaceId,
        normal,
        valid: clear && attached && (!hit.surface || fitsOnSurface(hit.surface, hit.point, size)),
      };
    }

    // A box rests on the surface, so its centre is pushed out along the normal
    // by half of its own thickness. The two in-plane axes snap to the grid.
    const halfAlong =
      Math.abs(normal[0]) * size[0] + Math.abs(normal[1]) * size[1] + Math.abs(normal[2]) * size[2];
    const centre = snapInPlane(hit.point, normal);
    scratchPoint.copy(centre).addScaledVector(hit.normal, halfAlong / 2);
    const position: [number, number, number] = [scratchPoint.x, scratchPoint.y, scratchPoint.z];

    return {
      kind: this.piece,
      position,
      size,
      surface: surfaceId,
      normal,
      valid: clear && (!hit.surface || fitsOnSurface(hit.surface, position, size)),
    };
  }

  /** Place a previewed piece into the map. Returns false when the spot is bad. */
  place(placement: BuildPlacement): boolean {
    if (!placement.valid) return false;
    if (placement.kind === 'drone') {
      this.document.drones.push({
        position: placement.position,
        surface: placement.surface,
        magnetic: this.magnetic,
      });
      return true;
    }
    if (placement.kind === 'waypoint') {
      const index = nearestDroneIndex(
        this.document,
        placement.position[0],
        placement.position[1],
        placement.position[2],
      );
      const drone = index === null ? undefined : this.document.drones[index];
      if (!drone) return false;
      return appendWaypoint(drone, placement.position);
    }
    this.document.assets.push({
      kind: placement.kind as AssetKind,
      position: placement.position,
      size: placement.size,
    });
    this.syncCollision();
    return true;
  }

  /**
   * Delete whatever was placed at a point. Returns true when something went.
   *
   * Snapping means a piece can sit up to half a cell from the raw aim point, so
   * a near miss still finds it: the player is aiming at the thing they can see,
   * not at its coordinates.
   */
  removeAt(point: Vector3): boolean {
    if (removeAssetAt(this.document, point.x, point.y, point.z)) {
      this.syncCollision();
      return true;
    }
    if (removeDroneAt(this.document, point.x, point.y, point.z)) return true;
    // A waypoint is a marker, not a solid, so it is matched by its own radius
    // before the loose "nearest piece" fallback could claim a nearby crate.
    if (removeWaypointAt(this.document, point.x, point.y, point.z)) return true;

    const nearestAsset = this.nearest(point, this.document.assets, (asset) => asset.position);
    if (nearestAsset !== null) {
      this.document.assets.splice(nearestAsset, 1);
      this.syncCollision();
      return true;
    }
    const nearestDrone = this.nearest(point, this.document.drones, (drone) => drone.position);
    if (nearestDrone !== null) {
      this.document.drones.splice(nearestDrone, 1);
      return true;
    }
    return false;
  }

  /** The index of the entry nearest a point, if one is inside the tolerance. */
  private nearest<T>(
    point: Vector3,
    entries: readonly T[],
    position: (entry: T) => [number, number, number],
  ): number | null {
    let best: number | null = null;
    let bestDistance = DELETE_TOLERANCE;
    entries.forEach((entry, index) => {
      const [x, y, z] = position(entry);
      const distance = Math.hypot(x - point.x, y - point.y, z - point.z);
      if (distance <= bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }
}

/** Which of the six faces a normal points at. */
export function faceOf(normal: Vector3): SurfaceId {
  const x = Math.abs(normal.x);
  const y = Math.abs(normal.y);
  const z = Math.abs(normal.z);
  if (y >= x && y >= z) return normal.y >= 0 ? 'floor' : 'ceiling';
  if (x >= z) return normal.x >= 0 ? 'left' : 'right';
  return normal.z >= 0 ? 'front' : 'back';
}

/** Snap a point to the middle of its grid cell along the surface's two axes. */
export function snapInPlane(point: Vector3, normal: readonly number[]): Vector3 {
  const snapped = point.clone();
  const snap = (value: number): number => Math.floor(value / CELL_SIZE) * CELL_SIZE + CELL_SIZE / 2;
  if (Math.abs(normal[0] ?? 0) < 0.5) snapped.x = snap(snapped.x);
  if (Math.abs(normal[1] ?? 0) < 0.5) snapped.y = snap(snapped.y);
  if (Math.abs(normal[2] ?? 0) < 0.5) snapped.z = snap(snapped.z);
  return snapped;
}

/**
 * True when a piece fits inside the surface it is mounted on.
 *
 * The "do not hang it off the edge of a wall" rule: the piece's extents are
 * projected onto the surface's own two axes, so one check covers a floor, a
 * wall and a ceiling.
 */
export function fitsOnSurface(
  surface: Surface,
  position: Vector3 | [number, number, number],
  size: readonly number[],
): boolean {
  const point = position instanceof Vector3 ? position : new Vector3(...position);
  const local = surface.toLocal(point, scratchLocal);
  const halfU =
    (Math.abs(surface.right.x) * size[0]! +
      Math.abs(surface.right.y) * size[1]! +
      Math.abs(surface.right.z) * size[2]!) /
    2;
  const halfV =
    (Math.abs(surface.forward.x) * size[0]! +
      Math.abs(surface.forward.y) * size[1]! +
      Math.abs(surface.forward.z) * size[2]!) /
    2;
  // A small margin so a piece flush with the wall is still legal.
  const margin = 0.02;
  return (
    local.u - halfU >= surface.uMin - margin &&
    local.u + halfU <= surface.uMax + margin &&
    local.v - halfV >= surface.vMin - margin &&
    local.v + halfV <= surface.vMax + margin
  );
}

/** True when a piece would sit on top of the player. */
export function overlapsBody(point: Vector3, size: readonly number[], body: Vector3): boolean {
  const reachX = size[0]! / 2 + 0.6;
  const reachY = size[1]! / 2 + 0.6;
  const reachZ = size[2]! / 2 + 0.6;
  return (
    Math.abs(point.x - body.x) < reachX &&
    Math.abs(point.y - body.y) < reachY &&
    Math.abs(point.z - body.z) < reachZ
  );
}
